/**
 * Football Rive input / text-run / event names.
 *
 * IMPORTANT: these are the GROUND-TRUTH names recovered by binary-introspecting
 * football.riv (Tools/rive), NOT the drifted constants in
 *   packages/amgfootball_v2/lib/presentation/rive/rive_inputs.dart
 * The Flutter constants (`betLeft`, `chipSelect`, `isSoundOn`, …) silently
 * no-op against the real file. Where the live file and the Flutter literal
 * differ we set BOTH (the riveLayer broadcasts to candidate names).
 *
 * football.riv artboards (all share state machine `Main`):
 *   Main          — game animation, cards, ball, result, history, balance text
 *   BettingLayer  — goal click zones (emits evtBetClick{betID}), full-screen
 *   ChipsLayer    — chip picker (emits evtClickChips{chipsAmt = 1-based index})
 */

export type RiveSide = 'left' | 'center' | 'right';

/** Main artboard — state-machine inputs (binary-confirmed). */
export const MainInputs = {
  stateMachine: 'Main',
  /** Nested per-goal card value: input `Card` at path Card{Left,Right,Middle}. */
  card: 'Card',
  cardPathLeft: 'CardLeft',
  cardPathRight: 'CardRight',
  cardPathCenter: 'CardMiddle', // NOTE: "CardMiddle", not "CardCenter"
  /** Top-level (non-nested) `Card` input on Main = card-reveal animation. */
  cardAnim: 'Card',
  ballShoot: 'intBallShoot', // 0=clear 1=armed 2..9=shot trajectory
  result: 'intResult', // 0=reset 1=win 2=lose
  startStopBet: 'intStartStopBet', // 1=start bet 2=no-more-bet
  history: 'History', // nested at paths History1..History10
  isMobile: 'isMobile',
  isSoundOn: 'IsSoundOn', // capital I — single shared name across artboards
} as const;

/** ChipsLayer artboard inputs. */
export const ChipsInputs = {
  stateMachine: 'Main',
  /** Real name `intChipSelect`; Flutter `chipSelect`/`ChipSelect` are fallbacks. */
  chipSelect: 'intChipSelect',
  chipSelectFallbacks: ['chipSelect', 'ChipSelect'] as const,
} as const;

/** Text-run names. Real (binary) names first; bloc-literal alternates as fallback. */
export const TextRuns = {
  // balance run is literally `balance` in the file; bloc writes `txtBalance`.
  balance: ['balance', 'txtBalance'] as const,
  username: ['username', 'txtUsername'] as const,
  round: ['round'] as const,
  totalBet: ['txtTotalBetAmount'] as const,
  leftBet: ['leftBetAmount'] as const,
  centerBet: ['centerBetAmount'] as const,
  rightBet: ['rightBetAmount'] as const,
  leftOdds: ['leftOdds'] as const,
  centerOdds: ['centerOdds'] as const,
  rightOdds: ['rightOdds'] as const,
  youWinAmount: ['YouWinAmount'] as const,
} as const;

/** Per-goal bet-amount text run. */
export const BET_TEXT_RUN: Record<RiveSide, readonly string[]> = {
  left: TextRuns.leftBet,
  center: TextRuns.centerBet,
  right: TextRuns.rightBet,
};

/** Per-goal odds text run. */
export const ODDS_TEXT_RUN: Record<RiveSide, readonly string[]> = {
  left: TextRuns.leftOdds,
  center: TextRuns.centerOdds,
  right: TextRuns.rightOdds,
};

/** Rive event names emitted by football.riv (binary-confirmed). */
export const RiveEventNames = {
  /** BettingLayer — number prop `betID`. */
  evtBetClick: 'evtBetClick',
  /** ChipsLayer — number prop `chipsAmt` (1-based chip INDEX, not value). */
  evtClickChips: 'evtClickChips',
  /** Main — number prop `betID` (bloc ignores; we log). */
  placeBetClick: 'placeBetClick',
  /** Main + ChipsLayer — string prop `parts` (bloc ignores; we log). */
  uiButtonClick: 'UIButtonClick',
} as const;
